import BillingOptions, {AccessFeatures} from '@/components/feature/BillingOptions';
import { useSearchParams, Link } from "react-router-dom";
import { usePlanCatalog } from "@/hooks/usePlanCatalog";
import PageShell from "@/components/feature/PageShell";
import SiteCTA from "@/components/feature/SiteCTA";
import RelatedLinks from "@/components/feature/RelatedLinks";
import Reveal from "@/components/base/Reveal";

const related = [
  { label: "Sınıflar", href: "/siniflar" },
  { label: "Sınavlara Hazırlık", href: "/sinavlar" },
  { label: "Yardım Merkezi", href: "/yardim" },
  { label: "Hakkımızda", href: "/hakkimizda" },
];

export default function Paketler() {
  const [params]=useSearchParams();
  const { plans, error, cycle, setCycle } = usePlanCatalog(params.get('billing')==='yearly'?'yearly':'monthly');
  const crumbs = [{ label: "Ana Sayfa", href: "/" }, { label: "Paketler" }];

  return (
    <PageShell crumbs={crumbs}>
      {error && <p role="alert" className="p-6 text-center text-red-700">Paketler şu anda yüklenemedi. Lütfen sayfayı yenileyin.</p>}
      {/* Hero */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20 border-b border-background-200">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <span className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700">
              <span className="w-5 h-px bg-accent-500" aria-hidden="true"></span>
              Paketler
            </span>
            <h1 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground-950 leading-tight max-w-2xl">
              İhtiyacına göre başla
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl">
              Kendi hızında mı çalışırsın, rehberli mi istersin, öğretmenle mi? İhtiyacına
              uyan düzeyi seç.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pt-8"><div className="rounded-xl border bg-amber-50 p-5"><h2 className="font-bold">Satın almadan önce içerik kapsamı</h2><p className="mt-2">Başlangıç kütüphanesi tüm sınıf ve derslerin tamamını kapsamaz. Hazır video arşivi henüz sunulmuyor. Paketlerde belirtilen uygulama erişimleri ile yayınlanmış içerikler kullanılabilir. Sınıfınıza uygun mevcut konuları inceleyin.</p><Link className="mt-3 inline-block underline" to="/egitimler">Mevcut eğitim içeriklerini gör</Link></div></section>
      {/* Planlar */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-4xl mx-auto mb-8"><BillingOptions cycle={cycle} onChange={setCycle}/></div>
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {plans.map((plan, i) => (
            <Reveal key={plan.id} delay={i * 80} className="h-full">
              <Link
                to={`/kayit?plan=${plan.id}&billing=${cycle}`}
                className={`group relative flex flex-col h-full p-6 rounded-lg border transition-colors ${
                  plan.highlight
                    ? "border-primary-500 bg-primary-50/50 hover:border-primary-600"
                    : "border-background-200 bg-background-50 hover:border-primary-300"
                }`}
              >
                {plan.highlight && (
                  <span className="absolute top-0 right-5 -translate-y-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap">
                    <i className="ri-star-fill text-xs" aria-hidden="true"></i>
                    En Popüler
                  </span>
                )}

                <h2 className="font-heading font-semibold text-lg text-foreground-950">
                  {plan.name}
                </h2>
                <p className="mt-1.5 text-sm text-foreground-600">{plan.description}</p>

                <div className="mt-5 flex items-baseline gap-1.5">
                  <span className="font-heading text-3xl font-bold text-foreground-950 tracking-tight">
                    {plan.price}
                  </span>
                  <span className="text-sm text-foreground-500">{plan.period}</span>
                </div>

                  <div className="mt-3 text-sm text-primary-700">{plan.oldPrice&&<del className="mr-2 text-foreground-500">{plan.oldPrice}</del>}{plan.billingNote}<br/>{plan.savings}</div>
                  <AccessFeatures merakli={!!plan.merakliEnabled} meeting={!!plan.meetingEnabled} voice={plan.voiceCoach} parental={plan.parentalControls}/>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {plan.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-2.5 text-sm text-foreground-800"
                    >
                      <i
                        className="ri-check-line text-primary-600 text-base shrink-0"
                        aria-hidden="true"
                      ></i>
                      {feature}
                    </li>
                  ))}
                </ul>

                <span
                  className={`mt-6 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-md text-sm font-semibold whitespace-nowrap transition-colors ${
                    plan.highlight
                      ? "bg-primary-500 text-background-50 group-hover:bg-primary-600"
                      : "bg-background-100 text-foreground-900 group-hover:bg-primary-500 group-hover:text-background-50"
                  }`}
                >
                  {plan.cta}
                  <i
                    className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  ></i>
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-sm text-foreground-500 text-center max-w-2xl mx-auto leading-relaxed">
          Yıllık tutar 12 aylık peşin ödemedir; aylık taksit değildir. Güncel tutar ödeme ekranında doğrulanır.
        </p>
      </section>

      {/* İlgili sayfalar */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
            İlgili sayfalar
          </h2>
          <div className="mt-6">
            <RelatedLinks items={related} />
          </div>
        </div>
      </section>

      <SiteCTA />
    </PageShell>
  );
}
